import { cn } from "~/lib/utils";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { DataGridHeaderCell } from "./DataGridHeaderCell";
import { Pagination } from "../Pagination";
import { ColumnMetadata, ColumnTypes, RowSize, RowSizes } from "./types";
import { useWindowSize } from "../../utils/useWindowSize";
import { HorizontalScrollbar } from "./HorizontalScrollbar";
import { WindowResizeObserver } from "../WindowResizeObserver";
import throttle from "lodash.throttle";
import debounce from "lodash.debounce";
import {
    ApiQuery,
    PaginationState,
    SortDirections,
} from "~/modules/filters/types";
import * as Analytics from "./analytics";
import { ScrollButton } from "./ScrollButton";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";
import { useAccessSegment } from "./useAccessSegment";
import { PaginationPager } from "../Pagination";
import { DataGridDummyRows } from "./DataGridDummyRows";

// // // //

function getNestedPropertyValue<RowType>(
    item: RowType,
    nestedProperty: keyof RowType | string,
): any {
    if (typeof nestedProperty !== "string") {
        return item[nestedProperty];
    }

    const properties = nestedProperty.split(".");
    let value: any = item;

    for (let property of properties) {
        value = value[property];
    }

    return value;
}

// // // //

export function DataGrid<RowType, SortableColumns>(props: {
    query: ApiQuery; // ENHANCEMENT - DataGrid.query should be an optional prop
    loading?: boolean;
    columns: ColumnMetadata<RowType, SortableColumns>[];
    rows: RowType[];
    paginationState?: PaginationState;
    onChangeQuery?: (updatedQuery: ApiQuery) => void;
    children: (childProps: {
        row: RowType;
        rowIndex: number;
        scrollLeft: number;
    }) => React.ReactNode;
    rowKey: keyof RowType | string; // ENHANCEMENT - add support for rowKeys generated from multiple values in a row (edge case where a value from a single key might not be reliably unique)
    bordered?: boolean;
    size?: RowSize;
    fixedHeader?: boolean;
    fixedHeaderClassName?: string;
    enableItemsPerPageSelect?: boolean;
    enablePagerPagination?: boolean;
    enableScrollButton?: boolean;
    enableScrollbar?: boolean;
    disablePagination?: boolean;
    analyticsTableName: string;
    flush?: boolean;
    showHeader?: boolean;
    includeDummyRows?: boolean;
    watermark?: boolean;
}) {
    const {
        query,
        rows,
        rowKey,
        columns,
        paginationState,
        loading = false,
        onChangeQuery,
        children,
        bordered = false,
        size = RowSizes.medium,
        enableItemsPerPageSelect = false,
        enableScrollButton = false,
        analyticsTableName,
        enableScrollbar = true,
        enablePagerPagination = false,
        flush = false,
        showHeader = true,
        includeDummyRows = false,
        watermark = false,
        disablePagination = false,
        fixedHeaderClassName = "top-nav-height",
    } = props;
    const { field, direction } = query.sort;

    // // // //

    const ref = useRef<HTMLDivElement>(null);
    const stickyHeaderRef = useRef<HTMLDivElement>(null);
    const windowSize = useWindowSize();

    const accessSegment = useAccessSegment();

    const [scrollLeftPosition, setScrollLeftPosition] = useState(0);

    // Stable throttled/debounced functions — created once, cancelled on unmount
    const throttledSetScrollLeft = useRef(
        throttle((val: number) => {
            setScrollLeftPosition(val);
        }, 200),
    ).current;

    const [showDropShadow, setShowDropShadow] = useState(true);

    // Set the initial state for showDropShadow based on the current width of the browser window
    useEffect(() => {
        if (ref.current) {
            // Show/hide the drop-shadow-sm
            const maxScrollLeft =
                ref.current.scrollWidth - ref.current.clientWidth;

            if (ref.current.scrollLeft > maxScrollLeft - 100) {
                setShowDropShadow(false);
            } else {
                setShowDropShadow(true);
            }
        }
    }, [windowSize.width, columns]);

    // Ref to hold the latest scroll handler logic (avoids stale closures)
    const handleScrollRef = useRef<(event: any) => void>(() => {});

    // Stable function identity for add/removeEventListener
    const stableHandleScroll = useCallback((event: any) => {
        handleScrollRef.current(event);
    }, []);

    const addHeaderEventListener = useRef(
        debounce(() => {
            stickyHeaderRef.current?.addEventListener(
                "scroll",
                stableHandleScroll,
            );
        }, 200),
    ).current;

    const addTableEventListener = useRef(
        debounce(() => {
            ref.current?.addEventListener("scroll", stableHandleScroll);
        }, 200),
    ).current;

    // Update the scroll handler logic every render (stable identity via stableHandleScroll)
    handleScrollRef.current = (event: any) => {
        const { target } = event;
        const maxScrollLeft = target.scrollWidth - target.clientWidth;

        if (target === ref.current) {
            if (stickyHeaderRef.current) {
                stickyHeaderRef.current.removeEventListener(
                    "scroll",
                    stableHandleScroll,
                );
                stickyHeaderRef.current.scrollLeft = target.scrollLeft;
                addHeaderEventListener();
            }
        } else if (target === stickyHeaderRef.current) {
            if (ref.current) {
                ref.current.removeEventListener("scroll", stableHandleScroll);
                ref.current.scrollLeft = target.scrollLeft;
                addTableEventListener();
            }
        }

        // Set scrollLeftPosition state
        throttledSetScrollLeft(target.scrollLeft);

        // Set showDropShadow
        if (target.scrollLeft > maxScrollLeft - 100) {
            setShowDropShadow(false);
        } else {
            setShowDropShadow(true);
        }
    };

    useEffect(() => {
        ref.current?.addEventListener("scroll", stableHandleScroll);
        stickyHeaderRef.current?.addEventListener("scroll", stableHandleScroll);
        return () => {
            ref.current?.removeEventListener("scroll", stableHandleScroll);
            stickyHeaderRef.current?.removeEventListener(
                "scroll",
                stableHandleScroll,
            );
            throttledSetScrollLeft.cancel();
            addHeaderEventListener.cancel();
            addTableEventListener.cancel();
        };
    }, []);

    // // // //

    // NOTE - this is commented out for performance reasons
    // We'll have to find a different approach, this is too expensive + error-prone
    // useEffect(() => {
    //     const handleScroll = () => {
    //         if (!stickyHeaderRef.current) {
    //             return;
    //         }
    //         const stickyHeaderBoundingRect =
    //             stickyHeaderRef.current.getBoundingClientRect();

    //         // TODO - how can we remove this hardcoded "64" value? (corresponds to "top-nav-height" variable)
    //         // Perhaps we can compare this value against the past value recorded for this?
    //         // The hardcoded "64" value doesn't work when zoomed in/out
    //         if (stickyHeaderBoundingRect.top === 64) {
    //             setIsSticky(true);
    //         } else {
    //             setIsSticky(false);
    //         }
    //     };

    //     window.addEventListener("scroll", handleScroll);

    //     return () => {
    //         window.removeEventListener("scroll", handleScroll);
    //     };
    // }, []);

    // // // //

    // Define className for table header row
    const tableHeaderClassName = cn("flex flex-row", {
        "divide-x": bordered,
    });

    // TODO - pull this out into a separate component?
    const headerContent =
        showHeader === false ? null : (
            <React.Fragment>
                {columns.map((c) => {
                    const contentClassName =
                        c.type === ColumnTypes.number ||
                        c.type === ColumnTypes.dollar ||
                        c.type === ColumnTypes.percent ||
                        c.type === ColumnTypes.percentChange ||
                        c.type === ColumnTypes.years ||
                        c.type === ColumnTypes.date ||
                        c.type === ColumnTypes.datetime ||
                        c.type === ColumnTypes.datetimeCompact ||
                        c.type === ColumnTypes.timeAgo ||
                        c.type === ColumnTypes.sparkline
                            ? "justify-end"
                            : undefined;

                    if (c.sortsOn) {
                        return (
                            <FeatureFlag
                                key={c.slug}
                                feature={c.featureFlag?.feature || undefined}
                                invert={c.featureFlag?.invert}
                            >
                                <DataGridHeaderCell
                                    width={c.width}
                                    maxWidth={c.maxWidth}
                                    loading={loading}
                                    label={c.label}
                                    slug={c.slug}
                                    tooltip={c.tooltip}
                                    size={size}
                                    sticky={c.sticky}
                                    scrollLeft={ref?.current?.scrollLeft || 0}
                                    flush={flush}
                                    access={c.access}
                                    accessSegment={accessSegment}
                                    direction={
                                        field === c.sortsOn
                                            ? direction
                                            : SortDirections.none
                                    }
                                    analyticsTableName={analyticsTableName}
                                    onChangeSort={(updatedSort) => {
                                        if (!onChangeQuery) {
                                            return;
                                        }

                                        Analytics.trackDataGridSort({
                                            tableName: analyticsTableName,
                                            direction: updatedSort,
                                            column: c.slug,
                                        });

                                        onChangeQuery({
                                            ...query,
                                            pagination: {
                                                ...query.pagination,
                                                page: 1,
                                            },
                                            sort: {
                                                direction: updatedSort,
                                                field:
                                                    updatedSort ===
                                                        SortDirections.none ||
                                                    updatedSort === undefined
                                                        ? ""
                                                        : (c.sortsOn as string),
                                            },
                                        });
                                    }}
                                    contentClassName={contentClassName}
                                />
                            </FeatureFlag>
                        );
                    }

                    return (
                        <FeatureFlag
                            feature={c.featureFlag?.feature || undefined}
                            invert={c.featureFlag?.invert}
                            key={c.slug}
                        >
                            <DataGridHeaderCell
                                width={c.width}
                                maxWidth={c.maxWidth}
                                loading={loading}
                                label={c.label}
                                slug={c.slug}
                                tooltip={c.tooltip}
                                size={size}
                                sticky={c.sticky}
                                scrollLeft={ref?.current?.scrollLeft || 0}
                                contentClassName={contentClassName}
                                flush={flush}
                                access={c.access}
                                accessSegment={accessSegment}
                                analyticsTableName={analyticsTableName}
                            />
                        </FeatureFlag>
                    );
                })}
            </React.Fragment>
        );

    // Define flags to conditionally show the pagination / scrollbar
    const showPagination =
        disablePagination === false &&
        paginationState !== undefined &&
        onChangeQuery !== undefined;

    const showScrollbar =
        enableScrollbar &&
        ref.current !== null &&
        ref.current.scrollWidth > ref.current.clientWidth;

    return (
        <div
            className={cn("overflow-clip w-full relative", {
                "border rounded-lg": !flush,
            })}
        >
            <div
                className={cn(
                    "w-6 -mr-6 z-100 transition-opacity bg-transparent h-full absolute right-0",
                    {
                        "opacity-0": !showDropShadow,
                        "opacity-100": showDropShadow,
                    },
                )}
                style={{
                    boxShadow: "rgba(20, 23, 28, 0.12) -10px 0px 10px", // $shadow-sm color
                    // boxShadow: "rgba(20, 23, 28, 0.16) -10px 0px 10px", // $shadow-md color
                    // boxShadow: "rgba(20, 23, 28, 0.24) -10px 0px 10px", $shadow-lg + $shadow-xl color
                }}
            />

            {/* Render FixedHeader */}
            {props.fixedHeader && showHeader && (
                <div
                    ref={stickyHeaderRef}
                    style={{
                        willChange: "scroll-position",
                        // scrollBehavior: "smooth",
                    }}
                    className={cn(
                        "hide-scrollbar overflow-x-auto overscroll-x-contain sticky shadow-xs transition-opacity z-40",
                        fixedHeaderClassName,
                        {
                            "border-b": true, // NOTE - this shouldn't be controlled by props.bordered
                            "rounded-lg rounded-bl-none rounded-br-none":
                                !flush,
                        },
                    )}
                >
                    <div
                        className={cn(
                            "inline-block min-w-full align-middle sticky",
                            fixedHeaderClassName,
                        )}
                    >
                        <table className="min-w-full bg-card">
                            <thead>
                                <tr className={tableHeaderClassName}>
                                    {headerContent}
                                </tr>
                            </thead>
                        </table>
                    </div>
                </div>
            )}

            <div
                className={cn("shadow-brand-sm bg-card overflow-clip", {
                    "rounded-tl-none rounded-tr-none": props.fixedHeader,
                    "rounded-lg": !flush,
                })}
            >
                <div
                    ref={ref}
                    style={{
                        willChange: "scroll-position",
                        // scrollBehavior: "smooth",
                    }}
                    className="hide-scrollbar overflow-x-auto overscroll-x-contain"
                >
                    <div className="inline-block min-w-full align-middle">
                        <table className="min-w-full">
                            {showHeader && (
                                <thead
                                    className={cn({
                                        hidden: props.fixedHeader,
                                        "border-b": true, // NOTE - this shouldn't be controlled by props.bordered
                                    })}
                                >
                                    <tr className={tableHeaderClassName}>
                                        {headerContent}
                                    </tr>
                                </thead>
                            )}
                            <tbody
                                className={cn("bg-card transition-opacity", {
                                    "opacity-50": loading,
                                    "divide-y": bordered,
                                    "datagrid-watermark": watermark,
                                })}
                            >
                                {rows.map((row, rowIndex) => (
                                    <React.Fragment
                                        key={`row-${getNestedPropertyValue(
                                            row,
                                            rowKey,
                                        )}`}
                                    >
                                        {children({
                                            row,
                                            rowIndex,
                                            scrollLeft: scrollLeftPosition,
                                        })}
                                    </React.Fragment>
                                ))}

                                {/* Render Dummy Rows */}
                                {includeDummyRows && paginationState && (
                                    <DataGridDummyRows
                                        rows={rows}
                                        columns={columns}
                                        paginationState={paginationState}
                                        bordered={bordered}
                                        size={size}
                                        analyticsTableName={analyticsTableName}
                                    />
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                {(showPagination || showScrollbar) && (
                    <div className="px-6 border-t bg-card sticky bottom-0 z-100 flex flex-col items-center">
                        {ref.current !== null && enableScrollbar && (
                            <div className="flex flex-col lg:flex-row items-start lg:items-center w-full gap-3 mt-0 lg:gap-2">
                                {/* ENHANCEMENT - control this via props */}
                                {/* ENHANCEMENT - move this into a separate file */}
                                {enableScrollButton && (
                                    <ScrollButton
                                        showDropShadow={showDropShadow}
                                        onClick={() => {
                                            // Track analytics event
                                            Analytics.trackClickScrollButton({
                                                direction: showDropShadow
                                                    ? "end"
                                                    : "front",
                                            });

                                            // Return if there's no table ref yet (just to be type safe)
                                            if (!ref.current) {
                                                return;
                                            }

                                            //
                                            if (showDropShadow) {
                                                ref.current.scrollLeft = 999_999;
                                                return;
                                            }
                                            ref.current.scrollLeft = 0;
                                        }}
                                    />
                                )}

                                {enableScrollbar && (
                                    <WindowResizeObserver>
                                        <HorizontalScrollbar
                                            isTablePaginated={
                                                disablePagination === true
                                                    ? false
                                                    : paginationState !==
                                                      undefined
                                            }
                                            // isTablePaginated={disablePagination}
                                            tableRef={ref.current}
                                            onChange={(newScrollLeft) => {
                                                Analytics.trackScroll({
                                                    tableName:
                                                        analyticsTableName,
                                                });

                                                if (ref.current) {
                                                    ref.current.scrollLeft =
                                                        newScrollLeft;
                                                }
                                            }}
                                        />
                                    </WindowResizeObserver>
                                )}
                            </div>
                        )}

                        {/* Only render Pagination when enabled */}
                        {showPagination && (
                            <>
                                {enablePagerPagination && (
                                    <PaginationPager
                                        loading={loading}
                                        showCount={false}
                                        paginationState={paginationState}
                                        className="my-[0.45rem] w-full"
                                        onChange={(updatedPaginationQuery) => {
                                            onChangeQuery({
                                                ...query,
                                                pagination: {
                                                    ...updatedPaginationQuery,
                                                },
                                            });
                                        }}
                                    />
                                )}

                                {!enablePagerPagination && (
                                    <Pagination
                                        loading={loading}
                                        disableControls={
                                            rows.length ===
                                            paginationState.resultCount
                                        }
                                        enableItemsPerPageSelect={
                                            enableItemsPerPageSelect
                                        }
                                        paginationState={paginationState}
                                        onChange={(updatedPaginationQuery) => {
                                            Analytics.trackPageChange({
                                                page: paginationState.page,
                                                perPage:
                                                    paginationState.perPage,
                                                tableName: analyticsTableName,
                                            });

                                            onChangeQuery({
                                                ...query,
                                                pagination: {
                                                    ...updatedPaginationQuery,
                                                },
                                            });
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
